/**
 * ModRoom — /admin/mod-room
 *
 * Staff-only private discussion area for admins + moderators.
 * Uses a single-page split view: left = thread list, right = selected
 * thread + replies + compose. Purpose-built (not the public forum).
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  ShieldCheck, MessagesSquare, Pin, Lock, Plus, Send, Loader2, ArrowLeft, Users,
} from 'lucide-react';

const relTime = (iso) => {
  try {
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60)    return `${secs}s ago`;
    if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return ''; }
};

const ModRoom = () => {
  const { API, user } = useContext(AuthContext);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [showComposer, setShowComposer] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mod-room/threads?limit=100`);
      setThreads(r.data.threads || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Mod Room');
    } finally { setLoading(false); }
  }, [API]);

  const loadThread = useCallback(async (id) => {
    setDetailLoading(true);
    setSelectedId(id);
    try {
      const r = await axios.get(`${API}/mod-room/threads/${id}`);
      setSelected(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load thread');
    } finally { setDetailLoading(false); }
  }, [API]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (threads.length && !selectedId) loadThread(threads[0].id);
  }, [threads, selectedId, loadThread]);

  const createThread = async (e) => {
    e && e.preventDefault();
    if (!newTitle.trim() || !newBody.trim()) { toast.error('Title and body are required'); return; }
    setPosting(true);
    try {
      const r = await axios.post(`${API}/mod-room/threads`, {
        title: newTitle.trim(), body_md: newBody.trim(),
      });
      toast.success('Thread posted');
      setNewTitle(''); setNewBody(''); setShowComposer(false);
      await loadThreads();
      loadThread(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post');
    } finally { setPosting(false); }
  };

  const submitReply = async (e) => {
    e && e.preventDefault();
    if (!replyBody.trim() || !selectedId) return;
    setPosting(true);
    try {
      await axios.post(`${API}/mod-room/threads/${selectedId}/replies`, { body_md: replyBody.trim() });
      setReplyBody('');
      loadThread(selectedId);
      loadThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reply');
    } finally { setPosting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="mod-room">
      <div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
        <Link to="/admin/portal" className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> Admin portal
        </Link>
        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-primary bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5">
          <ShieldCheck className="w-3 h-3" /> Staff-only · admin + moderator
        </div>
      </div>

      <div className="pro-card p-5 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
            <MessagesSquare className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Mod Room</div>
            <h1 className="font-heading text-2xl font-extrabold">Coordinate on tricky calls</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              A private space for admins and moderators to compare notes on borderline reports,
              share escalation history, and align on policy. Threads and replies never appear
              in the public feed.
            </p>
          </div>
          <Button onClick={() => setShowComposer((s) => !s)} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="mod-room-new-thread-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New thread
          </Button>
        </div>

        {showComposer && (
          <form onSubmit={createThread} className="mt-4 space-y-2" data-testid="mod-room-composer">
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Thread title" data-testid="mod-room-new-title" />
            <Textarea rows={4} value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="What do you want the team to weigh in on?" data-testid="mod-room-new-body" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setShowComposer(false)} className="rounded-full font-bold">Cancel</Button>
              <Button type="submit" disabled={posting} className="rounded-full font-bold" data-testid="mod-room-new-submit">
                {posting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</> : <><Send className="w-4 h-4 mr-2" /> Post</>}
              </Button>
            </div>
          </form>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Thread list */}
        <div className="pro-card p-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 px-1">
            Threads · {threads.length}
          </div>
          {loading ? (
            <div className="p-3 text-muted-foreground text-sm flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : threads.length === 0 ? (
            <div className="p-3 text-muted-foreground text-sm italic">No threads yet — start the first one.</div>
          ) : (
            <ul className="space-y-1">
              {threads.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => loadThread(t.id)}
                    className={`w-full text-left rounded-lg px-3 py-2 transition-colors ${selectedId === t.id ? 'bg-primary/10 border-l-2 border-primary' : 'hover:bg-muted/50 border-l-2 border-transparent'}`}
                    data-testid={`mod-room-thread-${t.id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      {t.is_pinned && <Pin className="w-3 h-3 text-primary" />}
                      {t.is_closed && <Lock className="w-3 h-3 text-muted-foreground" />}
                      <span className="font-semibold text-sm text-foreground truncate">{t.title}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      {t.author_email} · {t.reply_count} replies · {relTime(t.last_activity_at)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Selected thread */}
        <div className="md:col-span-2 pro-card p-4" data-testid="mod-room-detail">
          {detailLoading ? (
            <div className="p-3 text-muted-foreground text-sm flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : !selected ? (
            <div className="p-3 text-muted-foreground text-sm italic">Select a thread to read.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-heading text-xl font-extrabold">{selected.thread.title}</h2>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                    <Users className="w-3 h-3" /> {selected.thread.author_email}
                    <span className="text-[10px] uppercase tracking-widest font-bold bg-muted px-1.5 py-0.5 rounded">{selected.thread.author_role}</span>
                    <span>· {relTime(selected.thread.created_at)}</span>
                  </div>
                </div>
                {selected.thread.is_closed && (
                  <span className="text-[10px] uppercase tracking-widest font-bold bg-muted text-muted-foreground px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Closed
                  </span>
                )}
              </div>
              <div className="mt-3 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm whitespace-pre-wrap">
                {selected.thread.body_md}
              </div>

              <div className="mt-4 space-y-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Replies</div>
                {selected.replies.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No replies yet.</div>
                ) : selected.replies.map((r) => (
                  <div key={r.id} className="rounded-lg border border-border px-3 py-2" data-testid={`mod-room-reply-${r.id}`}>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{r.author_email}</span>
                      <span className="text-[10px] uppercase tracking-widest font-bold bg-muted px-1.5 py-0.5 rounded ml-1">{r.author_role}</span>
                      <span> · {relTime(r.created_at)}</span>
                    </div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{r.body_md}</div>
                  </div>
                ))}
              </div>

              {!selected.thread.is_closed && (
                <form onSubmit={submitReply} className="mt-4">
                  <Textarea rows={2} value={replyBody} onChange={(e) => setReplyBody(e.target.value)}
                            placeholder="Write a reply…" data-testid="mod-room-reply-input" />
                  <div className="mt-2 flex justify-end">
                    <Button type="submit" disabled={posting || !replyBody.trim()} className="rounded-full font-bold" data-testid="mod-room-reply-submit">
                      {posting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</> : <><Send className="w-4 h-4 mr-2" /> Reply</>}
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModRoom;
